import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from 'components/Loading';
import Text from 'components/Text';
import {
  REFETCH_UNREAD_COUNT_TIMEOUT,
  SIGNAL_EVENT,
  TEXT_STRING,
  routes,
  YARIKANO_CHAT_ICON_URL,
  YARIKANO_CHAT_TITLE,
} from '../../constants';
import { EChannelType, TChannel, TMessageDetail } from 'types';
import ChannelListItem from './ChannelListItem';
import { MessageEvent, SignalEvent } from 'pubnub';
import { usePubNub } from 'pubnub-react';
import { useUserStore } from 'store';
import AvatarChatGroup from 'components/AvatarGroup/AvatarChatGroup';
import { VirtuosoHandle, StateSnapshot } from 'react-virtuoso';
import VirtuosoList from 'components/VirtualizedList/VirtuosoList';
import {
  buildChannelListWithPinnedRows,
  EFakeChannelType,
  FakeChannelItem,
  trackYarikanoChatOpen,
} from 'utils';

type TLastMessageList = {
  [x: string]: TMessageDetail;
};

type Props = {
  isLoading?: boolean;
  channels: TChannel[];
  fetchMore: () => void;
  refetch: () => void;
  hasMore: boolean;
  isFetchingNextPage?: boolean;
  createChannelWithAdmin: (isAuto: boolean) => void;
  virtuosoState?: StateSnapshot | null;
  virtuosoRef?: React.RefObject<VirtuosoHandle>;
  onChannelClick?: () => void;
  isSearching?: boolean;
};

const ChannelList = ({
  isLoading,
  channels,
  fetchMore,
  hasMore,
  isFetchingNextPage = false,
  refetch,
  createChannelWithAdmin,
  virtuosoState,
  virtuosoRef,
  onChannelClick,
  isSearching = false,
}: Props) => {
  // Hooks
  const pubnub = usePubNub();
  const navigate = useNavigate();
  const { isAuthenticated, user, clientId } = useUserStore();
  const timeoutRef = useRef<NodeJS.Timeout>();

  // States
  const [lastMessageList, setLastMessageList] = useState<TLastMessageList>({});
  const [unreadMessageList, setUnreadMessageList] = useState<{
    [x: string]: number;
  }>({});
  const [pinnedChannel, setPinnedChannel] = useState<Record<string, string>>(
    {},
  );

  // Memo, callbacks
  const mapMessage = useCallback(
    (message: TMessageDetail) => {
      let url;
      if (message.message.file) {
        url = pubnub.getFileUrl({
          channel: message.channel,
          id: message.message.file.id,
          name: message.message.file.name,
        });
      }
      return { ...message, url: url };
    },
    [pubnub],
  );

  const updateUnread = useCallback(
    (channelId: string) => {
      setUnreadMessageList((prev) => ({
        ...prev,
        [channelId]:
          (prev[channelId] ||
            channels.find((channel) => channel.id === channelId)?.unreadCount ||
            0) + 1,
      }));
    },
    [channels],
  );

  const refetchChannels = useCallback(
    (channelId: string) => {
      if (!channels.some((channel) => channel.id === channelId)) {
        if (!timeoutRef.current) {
          timeoutRef.current = setTimeout(() => {
            refetch();
            setLastMessageList([]);
            setUnreadMessageList({});
            clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
          }, REFETCH_UNREAD_COUNT_TIMEOUT);
        }
        return true;
      }
      return false;
    },
    [channels, refetch],
  );

  const refetchLastMessages = useCallback(() => {
    pubnub
      .fetchMessages({
        channels: channels.map(({ id }) => id),
        count: 1,
        stringifiedTimeToken: true,
      })
      .then((res) => {
        const newState = Object.keys(res.channels).reduce<TLastMessageList>(
          (acc, cur) => {
            acc[cur] = res.channels[cur][0];
            return acc;
          },
          {},
        );
        setLastMessageList(newState);
      })
      .catch((err) => console.error(err));
  }, [pubnub, channels]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      const channelId = event.message.channel;
      if (!channelId) return;
      if (!refetchChannels(channelId)) {
        setLastMessageList((prev) => ({
          ...prev,
          [channelId]: mapMessage({
            channel: channelId,
            message: event.message,
            timetoken: event.timetoken,
            uuid: event.publisher,
          }),
        }));
        updateUnread(channelId);
      }
    },
    [mapMessage, updateUnread, refetchChannels],
  );

  const handleSignal = useCallback(
    (params: SignalEvent) => {
      const message = params.message;
      switch (message.event || message) {
        case SIGNAL_EVENT.DELETE_MESSAGE:
          refetchLastMessages();
          break;

        default:
          break;
      }
    },
    [refetchLastMessages],
  );

  const getUnreadCount = useCallback(
    (item: TChannel) =>
      (lastMessageList[item.id] || item.lastMessage)?.uuid !==
      (user?.uuid || clientId)
        ? (unreadMessageList[item.id] ?? item.unreadCount)
        : 0,
    [clientId, lastMessageList, unreadMessageList, user?.uuid],
  );

  const handlePinChannel = useCallback(
    (channelId: string, pinOrder: string) => {
      setPinnedChannel((prev) => ({ ...prev, [channelId]: pinOrder }));
    },
    [],
  );

  const channelsView = useMemo(() => {
    const isManagementChannel = (channel: TChannel) =>
      [EChannelType.MALE_MANAGEMENT].includes(channel.type);

    return channels
      .map((channel) => ({
        ...channel,
        lastMessage: lastMessageList[channel.id] || channel.lastMessage,
        isManagement: isManagementChannel(channel),
        pinOrder: pinnedChannel[channel.id] ?? channel.pinOrder,
      }))
      .sort((a, b) => {
        if (b.isManagement) {
          return 1;
        } else if (a.pinOrder && !b.pinOrder) {
          return -1;
        } else if (!a.pinOrder && b.pinOrder) {
          return 1;
        } else {
          return b?.lastMessage?.timetoken - a?.lastMessage?.timetoken;
        }
      });
  }, [channels, lastMessageList, pinnedChannel]);

  const handleChannelClick = useCallback(() => {
    onChannelClick?.();
  }, [onChannelClick]);

  const handleYarikanoClick = useCallback(() => {
    trackYarikanoChatOpen({ userId: user?.id });
    onChannelClick?.();
    navigate(routes.YARIKANO_CHAT, {
      state: { yarikanoEntry: 'message-list' },
    });
  }, [navigate, onChannelClick, user?.id]);

  // If the user is not logged in and has not chatted with the administrator before
  // Display the chat channel to contact the administrator
  const dataWithFakeChannels = useMemo(
    () =>
      buildChannelListWithPinnedRows({
        channelsView,
        isAuthenticated,
        showYarikano: !isSearching,
      }),
    [channelsView, isAuthenticated, isSearching],
  );

  // Effects
  useEffect(() => {
    const listenerParams = {
      message: handleMessage,
      signal: handleSignal,
    };
    pubnub.addListener(listenerParams);

    return () => {
      pubnub.removeListener(listenerParams);
    };
  }, [pubnub, handleMessage, handleSignal]);

  const renderItem = useCallback(
    (index: number, item: TChannel | FakeChannelItem) => {
      if (
        item.type === EFakeChannelType.ADMIN ||
        item.type === EFakeChannelType.SYSTEM
      ) {
        return (
          <FakeChannelRow
            onClick={() =>
              createChannelWithAdmin(item.type === EFakeChannelType.SYSTEM)
            }
            title={
              item.type === EFakeChannelType.ADMIN
                ? TEXT_STRING.COMMON.MGM_STAFF
                : TEXT_STRING.MESSAGE.AUTOMATED_CHAT
            }
            message={
              item.type === EFakeChannelType.SYSTEM
                ? TEXT_STRING.MESSAGE.FAKE_PREVIEW
                : undefined
            }
            isHighlight={item.type === EFakeChannelType.ADMIN}
          />
        );
      }

      if (item.type === EFakeChannelType.YARIKANO) {
        return (
          <FakeChannelRow
            onClick={handleYarikanoClick}
            title={YARIKANO_CHAT_TITLE}
            avatarUrl={YARIKANO_CHAT_ICON_URL}
          />
        );
      }

      return (
        <div className="relative overflow-hidden">
          {item ? (
            <ChannelListItem
              channel={item as TChannel}
              unreadCount={getUnreadCount(item as TChannel)}
              onPinChannel={handlePinChannel}
              onItemClick={handleChannelClick}
            />
          ) : (
            <ItemLoading index={index} />
          )}
        </div>
      );
    },
    [
      createChannelWithAdmin,
      getUnreadCount,
      handlePinChannel,
      handleChannelClick,
      handleYarikanoClick,
    ],
  );

  const renderLoading = useCallback(() => {
    return <ItemLoading index={dataWithFakeChannels.length} />;
  }, [dataWithFakeChannels.length]);

  if (isLoading) {
    return (
      <div className="my-[80%]">
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex-1">
      {isAuthenticated && !channels.length && (
        <Text className="my-8 tracking-widest" center bold>
          {TEXT_STRING.MESSAGE.START_MESSAGE}
        </Text>
      )}
      <VirtuosoList<TChannel | FakeChannelItem>
        virtuosoRef={virtuosoRef}
        data={dataWithFakeChannels}
        hasNext={hasMore}
        isFetchingNext={isFetchingNextPage}
        onReachEnd={fetchMore}
        renderItem={renderItem}
        renderLoading={renderLoading}
        className="h-full"
        restoreStateFrom={virtuosoState ?? undefined}
      />
    </div>
  );
};

const FakeChannelRow = ({
  onClick,
  title,
  message,
  isHighlight,
  avatarUrl,
}: {
  onClick: () => void;
  title: string;
  message?: string;
  isHighlight?: boolean;
  avatarUrl?: string;
}) => {
  return (
    <div
      className={`flex w-full gap-5 px-4 py-2 cursor-pointer ${
        isHighlight ? ' bg-primary-50 border-primary-200' : ''
      }`}
      onClick={onClick}
    >
      <AvatarChatGroup
        avatars={[{ url: avatarUrl || '/images/logo192.png' }]}
        size="w-[4.5rem]"
        border={isHighlight ? 'border-2 border-primary' : ''}
      />
      <div className="flex-1 self-center">
        <div className="flex">
          <Text bold className="line-clamp-1 break-all flex-1 text-primary">
            {title}
          </Text>
        </div>

        {message && (
          <div className="flex mt-2">
            <div className="flex-1 line-clamp-1 break-all text-neutral-600">
              <Text className="line-clamp-1 break-all">{message}</Text>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ItemLoading = ({ index }: { index: number }) => {
  const animation =
    index % 2 === 0 ? `animate-pulse` : `animate-pulse [animation-delay:-0.1s]`;
  return (
    <div className={`flex w-full px-4 py-2`}>
      <div className={`h-16 w-16 rounded-full bg-gray-300 mr-3 ${animation}`} />
      <div
        className={`flex-1 rounded-xl border border-neutral-200 bg-gray-300 text-gray-300 ${animation}`}
      >
        ...Loading
      </div>
    </div>
  );
};

export default ChannelList;
